import React, { useCallback, useMemo } from 'react';
import { getLocale, useParams } from 'umi';
import { Segment } from 'tea-component';

import Service from '@/pages/dashboard/components/PageContainer/Service';
import type { ComponentMetricsItemType } from '@/pages/dashboard/components/PageContainer/type';
import {
  TECHLAND_GAME_CODE,
  TECHLAND_SCOPE_GROSS,
  TECHLAND_SCOPE_NET,
  TECHLAND_STUDIO_CODE,
} from '@home/pages/studio/home/_constant';
import { getName } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
import classNames from 'classnames';
import { omit } from 'lodash-es';

import StudioTableDownloadShell from '@home/pages/studio/home/components/StudioTableDownloadShell';
import { computeFilterDateRangeFromEnd } from '@home/pages/studio/home/utils/studioFilterDateRange';
import {
  applySpecialDealsFlag,
  mergeSpecialDealsTrackData,
} from '@home/pages/studio/home/utils/specialDealsRequest';

import './index.scss';

type TCustomFilterRow = {
  key?: string;
  filter_list?: unknown[];
};

const DISTRIBUTION_FILTERS_TEMPLATE: Record<string, unknown> = {
  market_list: ['255'],
  os_list: ['255'],
  category_list: ['255'],
  product_list: ['255'],
  zone_list: ['255'],
  channel_list: ['255'],
  lang_list: ['255'],
  custom_filters: [
    { key: 'bundle', filter_list: ['255'] },
    { key: 'payment_type', filter_list: ['Paid only'] },
    { key: 'catalog', filter_list: ['255'] },
  ],
};

function hasUsableFilterList(row: TCustomFilterRow | undefined): boolean {
  return (
    Array.isArray(row?.filter_list) && (row?.filter_list?.length ?? 0) > 0
  );
}

function mergeCustomFilters(
  base: TCustomFilterRow[],
  incoming: TCustomFilterRow[],
): TCustomFilterRow[] {
  const m = new Map<string, TCustomFilterRow>();
  base.forEach(row => row?.key && m.set(row.key, row));
  incoming.forEach(row => {
    if (!row?.key) {
      return;
    }
    if (hasUsableFilterList(row)) {
      m.set(row.key, row);
    }
  });
  return Array.from(m.values());
}

function mergeDistributionFilters(
  inputFilters: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
  const input = inputFilters && typeof inputFilters === 'object' ? inputFilters : {};
  const tplCf = DISTRIBUTION_FILTERS_TEMPLATE.custom_filters;
  const baseCf: TCustomFilterRow[] = Array.isArray(tplCf)
    ? (tplCf as TCustomFilterRow[])
    : [];
  const rawInCf = (input as { custom_filters?: unknown; }).custom_filters;
  const inCf = Array.isArray(rawInCf) ? (rawInCf as TCustomFilterRow[]) : [];
  return {
    ...DISTRIBUTION_FILTERS_TEMPLATE,
    ...input,
    custom_filters: mergeCustomFilters(baseCf, inCf),
  };
}

function parseMetricNumber(m: unknown): number | null {
  if (m === null || m === undefined) {
    return null;
  }
  if (typeof m === 'number') {
    return Number.isFinite(m) ? m : null;
  }
  if (typeof m === 'object' && m !== null) {
    if (!('value' in m)) {
      return null;
    }
    const v = (m as { value?: unknown; }).value;
    if (v === '' || v === null || v === undefined) {
      return null;
    }
    const n = parseFloat(String(v));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** overall_performance 指标对象上的环比 dod，与 Performance 表 formatData 一致 */
function parseMetricDod(m: unknown): number | null {
  if (m === null || m === undefined) {
    return null;
  }
  if (typeof m === 'object' && m !== null && 'dod' in m) {
    const d = (m as { dod?: unknown; }).dod;
    if (d === '' || d === null || d === undefined) {
      return null;
    }
    const n = parseFloat(String(d));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function applyMetricShares(
  rows: Record<string, unknown>[],
  keys: string[],
): Record<string, unknown>[] {
  const totals: Record<string, number> = {};
  keys.forEach(k => {
    totals[k] = rows.reduce((s, r) => {
      const v = r[k];
      return s + (typeof v === 'number' && Number.isFinite(v) ? v : 0);
    }, 0);
  });
  return rows.map(r => {
    const o = { ...r };
    keys.forEach(k => {
      const t = totals[k];
      const v = r[k];
      o[`${k}__share`] =
        t > 0 && typeof v === 'number' && Number.isFinite(v) ? v / t : null;
    });
    return o;
  });
}

function isGlobalGeoDisplayLabel(value: unknown): boolean {
  const raw = String(value ?? '').trim();
  if (raw === '全球') {
    return true;
  }
  return raw.toLowerCase() === 'global';
}

function sortGlobalLast(
  rows: Record<string, unknown>[],
  dimKey: 'region_name' | 'country_name',
): Record<string, unknown>[] {
  const isGlobal = (r: Record<string, unknown>) =>
    isGlobalGeoDisplayLabel(r[dimKey]);
  const others = rows.filter(r => !isGlobal(r));
  const globals = rows.filter(r => isGlobal(r));
  return [...others, ...globals];
}

/**
 * 从接口 `dimension_name` 取中英文展示名；`country_name_cn`/`country_name_en`（或 region 对应键）都为空时再回退到 legacy 字段。
 */
function pickLocalizedFromDimension(
  dimensionName: unknown,
  legacy: unknown,
  cnKey: 'country_name_cn' | 'region_name_cn',
  enKey: 'country_name_en' | 'region_name_en',
  preferEn: boolean,
): string {
  const d =
    dimensionName && typeof dimensionName === 'object'
      ? (dimensionName as Record<string, unknown>)
      : undefined;
  const rawCn = d?.[cnKey];
  const rawEn = d?.[enKey];
  const cn =
    rawCn !== null && rawCn !== undefined && String(rawCn).trim() !== ''
      ? String(rawCn).trim()
      : '';
  const en =
    rawEn !== null && rawEn !== undefined && String(rawEn).trim() !== ''
      ? String(rawEn).trim()
      : '';
  if (!cn && !en) {
    return legacy !== null && legacy !== undefined ? String(legacy) : '';
  }
  if (preferEn) {
    return en || cn;
  }
  return cn || en;
}

/**
 * 接口或 dimension 中文段可能仍返回 others / all / global（任意大小写），按当前语言映射为规范文案。
 */
function mapGeoSpecialDisplayLabel(text: string, preferEn: boolean): string {
  const t = String(text ?? '').trim();
  if (t === '') {
    return t;
  }
  const lower = t.toLowerCase();
  if (lower === 'others') {
    return preferEn ? 'Others' : '其他';
  }
  if (lower === 'all') {
    return preferEn ? 'All' : '全部';
  }
  if (lower === 'global') {
    return preferEn ? 'Global' : '全球';
  }
  return text;
}

function resolveGeoDisplayName(
  dimensionName: unknown,
  legacy: unknown,
  cnKey: 'country_name_cn' | 'region_name_cn',
  enKey: 'country_name_en' | 'region_name_en',
  preferEn: boolean,
): string {
  return mapGeoSpecialDisplayLabel(
    pickLocalizedFromDimension(
      dimensionName,
      legacy,
      cnKey,
      enKey,
      preferEn,
    ),
    preferEn,
  );
}

type TGeoTab = 'by_region' | 'by_country';

/** 行内标记：与 Service 缓存的数据一起存在，切换语言时在渲染层重算展示名 */
const STUDIO_GEO_LEGACY_REGION = '__region_name_legacy';
const STUDIO_GEO_LEGACY_COUNTRY = '__country_name_legacy';

/**
 * 按当前语言重算 region/country 展示列并做 Global 置底（不触发重新请求）。
 */
function patchRegionDistributionDataForLocale(
  data: Record<string, unknown> | undefined,
  dataKey: string,
  geoTab: TGeoTab,
  preferEn: boolean,
): Record<string, unknown> | undefined {
  if (!data) {
    return data;
  }
  type TRegionDistPack = { data?: Record<string, unknown>[]; };
  const pack = data[dataKey] as TRegionDistPack | undefined;
  const rows = pack?.data;
  if (!Array.isArray(rows) || !rows.length) {
    return data;
  }
  const first = rows[0];
  if (
    !first ||
    typeof first !== 'object' ||
    !(STUDIO_GEO_LEGACY_REGION in first)
  ) {
    return data;
  }
  const withNames = rows.map(row => {
    const r = row as Record<string, unknown>;
    return {
      ...r,
      region_name: resolveGeoDisplayName(
        r.dimension_name,
        r[STUDIO_GEO_LEGACY_REGION],
        'region_name_cn',
        'region_name_en',
        preferEn,
      ),
      country_name: resolveGeoDisplayName(
        r.dimension_name,
        r[STUDIO_GEO_LEGACY_COUNTRY],
        'country_name_cn',
        'country_name_en',
        preferEn,
      ),
    };
  });
  const dim: 'region_name' | 'country_name' =
    geoTab === 'by_country' ? 'country_name' : 'region_name';
  const sorted = sortGlobalLast(withNames, dim);
  return {
    ...data,
    [dataKey]: {
      ...pack,
      data: sorted,
    },
  };
}

/**
 * Service 下发 data 后、DownloadShell 之前：随 getLocale() 同步表格中的地理名称列。
 */
const StudioRegionDistributionDataByLocale: React.FC<{
  dataKey: string;
  geoTab: TGeoTab;
  data?: Record<string, unknown>;
  filterLoading?: boolean;
  children?: React.ReactNode;
}> = ({ dataKey, geoTab, data, filterLoading, children }) => {
  const isEN = getLocale() === LANGUAGE_EN;
  const dataOut = useMemo(
    () => patchRegionDistributionDataForLocale(data, dataKey, geoTab, isEN),
    [data, dataKey, geoTab, isEN],
  );
  return (
    <>
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) {
          return child;
        }
        const shellInject: {
          data: Record<string, unknown> | undefined;
          filterLoading: boolean | undefined;
        } = {
          data: dataOut,
          filterLoading,
        };
        return React.cloneElement(child, shellInject as never);
      })}
    </>
  );
};

export interface ISegmentOption {
  value: string;
  textEn: string;
  textCn: string;
}

/** 单 scope + 单地理 Tab：请求 metrics / sort_by、源指标名、表格列 */
export interface IRegionScopeGeoTabConfig {
  metrics: string[];
  sortBy: string;
  revenueSourceMetric: string;
  unitsSourceMetric: string;
  componentMetrics: ComponentMetricsItemType[];
}

/** 第一层 scope（与 Filter 一致），第二层 by_region / by_country */
export type TRegionConfigByScope = Record<string, Record<TGeoTab, IRegionScopeGeoTabConfig>>;

export interface IStudioRegionDistributionBlockProps {
  data?: Record<string, unknown>;
  changeData?: (key: string, value: unknown) => void;
  children?: React.ReactNode;
  filter?: Record<string, unknown>;
  filterVal?: Record<string, unknown>;
  filterLoading?: boolean;
  /** 可配置，默认与 DL2 Techland 一致 */
  api?: string;
  dataKey?: string;
  gameCode?: string;
  studioCode?: string;
  segmentOptions?: ISegmentOption[];
  /** scope → 地理 Tab → 请求与列（与 StudioBundlePerformanceBlock 分层思路一致） */
  regionConfigByScope: TRegionConfigByScope;
  titleEn?: string;
  titleCn?: string;
  downloadFileNameEn?: string;
  downloadFileNameCn?: string;
  className?: string;
  style?: React.CSSProperties;
  /** 透传给 Service，开关打开时在请求中追加 special deals logical_flag */
  appendSpecialDealsFlag?: boolean;
}

function resolveTechlandScopeKey(scope: unknown): string {
  if (scope === TECHLAND_SCOPE_GROSS || scope === 'gross_revenue') {
    return TECHLAND_SCOPE_GROSS;
  }
  return TECHLAND_SCOPE_NET;
}

const DEFAULT_API = '/api/v2/dashboard/studio/specific/overall_performance';
const DEFAULT_DATA_KEY = 'by_region_distribution_dltb';

function buildStudioBlockFilterSignature(
  filter: Record<string, unknown> | undefined,
  filterInitData: unknown,
): string {
  const f = filter ?? {};
  try {
    return JSON.stringify({
      scope: f.scope,
      end_time: f.end_time,
      date_type: f.date_type,
      filterInitData: filterInitData ?? null,
    });
  } catch {
    return `${String(f.scope)}-${String(f.end_time)}`;
  }
}

type TStudioRegionDistributionTabPaneProps = {
  geoTab: TGeoTab;
  isActive: boolean;
  serviceData: Record<string, unknown>;
  regionConfigByScope: TRegionConfigByScope;
  gameCode: string;
  studioCode: string;
  api: string;
  dataKey: string;
  filterLoading: boolean;
  segment: React.ReactNode;
  titleEn: string;
  titleCn: string;
  downloadFileNameEn?: string;
  downloadFileNameCn?: string;
  children?: React.ReactNode;
  appendSpecialDealsFlag?: boolean;
};

const StudioRegionDistributionTabPane: React.FC<
TStudioRegionDistributionTabPaneProps
> = ({
  geoTab,
  isActive,
  serviceData,
  regionConfigByScope,
  gameCode,
  studioCode,
  api,
  dataKey,
  filterLoading,
  segment,
  titleEn,
  titleCn,
  downloadFileNameEn,
  downloadFileNameCn,
  children,
  appendSpecialDealsFlag,
}) => {
  const beforeRequestTab = useCallback(
    (reqData: any) => {
      const fv = reqData?.filterVal ?? serviceData.filter;
      const filterInitData = reqData?.filterInitData ?? {};
      const tab = geoTab;
      const sk = resolveTechlandScopeKey(fv?.scope);
      const cfg = regionConfigByScope[sk]?.[tab];
      if (!cfg?.metrics?.length) {
        return false;
      }

      const range = computeFilterDateRangeFromEnd(fv?.end_time, fv?.date_type);
      if (!range) {
        return false;
      }

      const dateType = fv?.date_type;
      let period = 'daily';
      if (dateType === 'monthly') {
        period = 'monthly';
      } else if (dateType === 'weekly') {
        period = 'weekly';
      }

      const filtersMerged = mergeDistributionFilters(
        filterInitData as Record<string, unknown>,
      );

      return applySpecialDealsFlag(
        {
          game_code: gameCode,
          start_date: range.start,
          end_date: range.end,
          period,
          metrics: cfg.metrics,
          accumulation:
            tab === 'by_region' ? { by_region: true } : { by_country: true },
          sort_by: cfg.sortBy,
          page: 1,
          page_size: 999,
          desc: true,
          filters: {
            ...filtersMerged,
            studioCode,
          },
          studio_code: studioCode,
        },
        reqData,
        appendSpecialDealsFlag,
      );
    },
    [
      appendSpecialDealsFlag,
      gameCode,
      geoTab,
      regionConfigByScope,
      serviceData.filter,
      studioCode,
    ],
  );

  const formatDataTab = useCallback(
    (resData: any, filterVal: any) => {
      const list = resData?.metrics_list || [];
      const sk = resolveTechlandScopeKey(filterVal?.scope);
      const cfg = regionConfigByScope[sk]?.[geoTab];
      if (!cfg) {
        return [];
      }
      const revSrc = cfg.revenueSourceMetric;
      const untSrc = cfg.unitsSourceMetric;

      const preferEnAtFetch = getLocale() === LANGUAGE_EN;
      const rows: Record<string, unknown>[] = list.map((item: any) => {
        const metrics = item?.metrics || {};
        const dimName = item?.dimension_name;
        const legacyRegion = item?.region_name ?? '';
        const legacyCountry = item?.country_name ?? '';
        const rawCc =
          item?.country_code ??
          item?.countryCode ??
          item?.country ??
          item?.iso_code ??
          item?.market_code ??
          item?.country_id;
        const resolvedCountryCode =
          rawCc !== null &&
          rawCc !== undefined &&
          String(rawCc).trim() !== ''
            ? String(rawCc).trim()
            : '';
        return {
          dimension_name: dimName ?? null,
          [STUDIO_GEO_LEGACY_REGION]: legacyRegion,
          [STUDIO_GEO_LEGACY_COUNTRY]: legacyCountry,
          region_name: resolveGeoDisplayName(
            dimName,
            legacyRegion,
            'region_name_cn',
            'region_name_en',
            preferEnAtFetch,
          ),
          country_name: resolveGeoDisplayName(
            dimName,
            legacyCountry,
            'country_name_cn',
            'country_name_en',
            preferEnAtFetch,
          ),
          country_code: resolvedCountryCode,
          country: resolvedCountryCode,
          countryCode: resolvedCountryCode,
          lifetime_profit: parseMetricNumber(metrics[revSrc]),
          lifetime_profit_dod: parseMetricDod(metrics[revSrc]),
          lifetime_full_game_net_units: parseMetricNumber(metrics[untSrc]),
          lifetime_full_game_net_units_dod: parseMetricDod(metrics[untSrc]),
        };
      });

      const dim: 'region_name' | 'country_name' =
        geoTab === 'by_country' ? 'country_name' : 'region_name';
      const withShare = applyMetricShares(rows, [
        'lifetime_profit',
        'lifetime_full_game_net_units',
      ]);
      return sortGlobalLast(withShare, dim);
    },
    [geoTab, regionConfigByScope],
  );

  const mergedData = useMemo(
    () => ({
      ...serviceData,
      regionDistributionTab: geoTab,
    }),
    [geoTab, serviceData],
  );

  const branchMetrics =
    regionConfigByScope[resolveTechlandScopeKey(
      (serviceData.filter as { scope?: string; } | undefined)?.scope,
    )]?.[geoTab]?.componentMetrics ?? [];

  const tableChildren = useMemo(() => {
    return React.Children.map(children, child => {
      if (!React.isValidElement(child)) {
        return child;
      }
      const el = child as React.ReactElement<{
        config?: {
          props?: Record<string, unknown>;
        };
      }>;
      const cfg = el.props?.config;
      if (!cfg || typeof cfg !== 'object') {
        return child;
      }
      const baseCprops =
        cfg.props && typeof cfg.props === 'object' ? cfg.props : {};
      const prevWrap =
        (baseCprops.metricsCardWrapProps as Record<string, unknown>) || {};
      const prevCbt =
        (baseCprops.commonBaseTableProps as Record<string, unknown>) || {};
      return React.cloneElement(el, {
        config: {
          ...cfg,
          props: {
            ...baseCprops,
            dataKey: (baseCprops.dataKey as string | undefined) ?? dataKey,
            metricsCardWrapProps: {
              ...omit(prevWrap, 'contentHeader'),
              titleEn,
              titleCn,
              ...(isActive && segment ? { contentHeader: segment } : {}),
            },
            commonBaseTableProps: {
              ...prevCbt,
              componentMetrics: branchMetrics,
            },
          },
        },
      });
    });
  }, [branchMetrics, children, dataKey, isActive, segment, titleCn, titleEn]);

  return (
    <div
      className={classNames(
        'dbh-studio-region-tab-pane',
        !isActive && 'dbh-studio-region-tab-pane--hidden',
      )}
    >
      <Service
        api={api}
        dataKey={dataKey}
        beforeRequest={beforeRequestTab}
        formatData={formatDataTab}
        rawData
        trackData={mergeSpecialDealsTrackData(
          ['filterInitData'],
          appendSpecialDealsFlag,
        )}
        data={mergedData}
        filterLoading={filterLoading}
      >
        <StudioRegionDistributionDataByLocale
          dataKey={dataKey}
          geoTab={geoTab}
        >
          <StudioTableDownloadShell
            dataKey={dataKey}
            titleEn={titleEn}
            titleCn={titleCn}
            downloadFileNameEn={downloadFileNameEn}
            downloadFileNameCn={downloadFileNameCn}
          >
            {tableChildren}
          </StudioTableDownloadShell>
        </StudioRegionDistributionDataByLocale>
      </Service>
    </div>
  );
};

/**
 * Region Distribution：组件内 Service + beforeRequest/formatData；
 * CommonTable 由 page 配置 children 声明；标题、指标、Segment、接口路径等可由 props 配置。
 */
const StudioRegionDistributionBlock: React.FC<IStudioRegionDistributionBlockProps> = ({
  data: propsData,
  changeData,
  children,
  filter: filterFromParent,
  filterVal: filterValFromParent,
  filterLoading: propsFilterLoading,
  api = DEFAULT_API,
  dataKey = DEFAULT_DATA_KEY,
  gameCode: gameCodeProp,
  studioCode: studioCodeProp,
  segmentOptions = [],
  regionConfigByScope,
  titleEn = 'Region Distribution',
  titleCn = 'Region Distribution',
  downloadFileNameEn,
  downloadFileNameCn,
  className,
  style,
  appendSpecialDealsFlag,
}) => {
  const routeParams = useParams() as { studioId?: string; };
  const { studioId } = routeParams;
  const studioCode = studioCodeProp || studioId || TECHLAND_STUDIO_CODE;
  const gameCode = gameCodeProp || TECHLAND_GAME_CODE;
  const locale = getLocale();
  const isEN = locale === LANGUAGE_EN;

  const geoTab: TGeoTab =
    propsData?.regionDistributionTab === 'by_country' ? 'by_country' : 'by_region';

  const serviceData = useMemo(() => {
    const fromPropsData = propsData as
      | { filter?: Record<string, unknown>; }
      | undefined;
    return {
      ...(propsData || {}),
      filter:
        filterFromParent ??
        filterValFromParent ??
        fromPropsData?.filter,
    };
  }, [propsData, filterFromParent, filterValFromParent]);

  const filterObj = serviceData.filter as { scope?: string; } | undefined;
  const filterScope = filterObj?.scope;
  const scopeKey = resolveTechlandScopeKey(filterScope);

  const filterSignature = useMemo(
    () =>
      buildStudioBlockFilterSignature(
        serviceData.filter as Record<string, unknown> | undefined,
        serviceData.filterInitData,
      ),
    [serviceData.filter, serviceData.filterInitData],
  );

  const cacheResetKey = `${dataKey}-${gameCode}-${studioCode}-${filterSignature}`;

  const geoTabValues = useMemo((): TGeoTab[] => {
    const vals = segmentOptions
      .map(o => o.value)
      .filter((v): v is TGeoTab => v === 'by_region' || v === 'by_country');
    return vals.length > 0 ? vals : ['by_region', 'by_country'];
  }, [segmentOptions]);

  const teaSegmentOptions = useMemo(
    () =>
      segmentOptions.map(o => ({
        value: o.value,
        text: getName(isEN, o.textEn, o.textCn) || o.value,
      })),
    [isEN, segmentOptions],
  );

  const segment = useMemo(
    () => (
      <Segment
        className="dbh-studio-region-distribution-segment"
        value={geoTab}
        onChange={(v: string) => {
          changeData?.('regionDistributionTab', v);
        }}
        options={teaSegmentOptions}
      />
    ),
    [changeData, geoTab, teaSegmentOptions],
  );

  const segmentForHeader =
    teaSegmentOptions.length > 0 ? segment : null;

  return (
    <div
      className={classNames('dbh-studio-region-distribution-block', className)}
      style={style}
    >
      <div key={cacheResetKey} className="dbh-studio-region-tab-panes">
        {geoTabValues.map(tab => (
          <StudioRegionDistributionTabPane
            key={`${dataKey}-${tab}-${scopeKey}`}
            geoTab={tab}
            isActive={geoTab === tab}
            serviceData={serviceData}
            regionConfigByScope={regionConfigByScope}
            gameCode={gameCode}
            studioCode={studioCode}
            api={api}
            dataKey={dataKey}
            filterLoading={propsFilterLoading ?? false}
            segment={segmentForHeader}
            titleEn={titleEn}
            titleCn={titleCn}
            downloadFileNameEn={downloadFileNameEn}
            downloadFileNameCn={downloadFileNameCn}
            appendSpecialDealsFlag={appendSpecialDealsFlag}
          >
            {children}
          </StudioRegionDistributionTabPane>
        ))}
      </div>
    </div>
  );
};

export default StudioRegionDistributionBlock;
